import type { UIMessage } from 'ai';

/**
 * ``chat_messages.role`` the agent writes for a human support agent's reply (kintsugi-agent
 * ``support_inbound_replies``, PS-1357). The AI SDK ``UIMessage.role`` union has no such value,
 * so these rows travel as ``role: 'system'`` (the one role the agent drops from a client-sent
 * transcript, so a reply is never replayed to the model) tagged with ``metadata.kinHumanAgent``.
 */
export const HUMAN_AGENT_MESSAGE_ROLE = 'human_agent';

export type KinHumanAgentMetadata = {
  /** Support agent display name from DevRev (e.g. "Sarah Rodriguez"); null when DevRev sent none. */
  displayName: string | null;
};

const HUMAN_AGENT_METADATA_KEY = 'kinHumanAgent';

/**
 * Copy for the wait state (session open, no agent reply yet) and the inbound-reply error state
 * is owned by PS-1350 and not final. Placeholders only; do not treat as approved copy.
 */
export const HUMAN_AGENT_WAITING_TEXT_PLACEHOLDER = 'Waiting for a support agent to reply...';
export const HUMAN_AGENT_REPLY_ERROR_TEXT_PLACEHOLDER =
  'We could not load the latest reply from support. Please refresh to try again.';
/** Shown as the speaker label when DevRev sent no display name for the agent. */
export const HUMAN_AGENT_FALLBACK_LABEL = 'Support agent';

export function humanAgentUiMessage(
  row: { content: string; author_display_name?: string | null },
  id: string
): UIMessage {
  const displayName = row.author_display_name?.trim() ? row.author_display_name.trim() : null;
  const metadata: Record<string, KinHumanAgentMetadata> = {
    [HUMAN_AGENT_METADATA_KEY]: { displayName },
  };
  return {
    id,
    role: 'system',
    parts: [{ type: 'text', text: row.content }],
    metadata,
  };
}

export function getHumanAgentMetadata(message: UIMessage): KinHumanAgentMetadata | null {
  const raw = message.metadata;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const tag = (raw as Record<string, unknown>)[HUMAN_AGENT_METADATA_KEY];
  if (!tag || typeof tag !== 'object' || Array.isArray(tag)) return null;
  const name = (tag as { displayName?: unknown }).displayName;
  return { displayName: typeof name === 'string' && name.trim() ? name.trim() : null };
}

export function isHumanAgentUiMessage(message: UIMessage): boolean {
  return message.role === 'system' && getHumanAgentMetadata(message) !== null;
}
